import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { statsApi, bookingApi, opportunityApi } from '../lib/api';
import ProfileCompletionBanner from '../components/ProfileCompletionBanner';
import {
  Calendar, DollarSign, Star, TrendingUp, Clock, ArrowRight,
  Briefcase, Users, Plus, Loader2
} from 'lucide-react';
import { format } from 'date-fns';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsRes, bookingsRes] = await Promise.all([
        statsApi.getDashboard(),
        bookingApi.getMy()
      ]);
      setStats(statsRes.data);
      setRecentBookings(bookingsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'completed': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'cancelled': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Profile Completion Banner for Bookees */}
        <ProfileCompletionBanner />
        
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-muted-foreground">
            {user?.user_type === 'talent' 
              ? 'Manage your bookings and grow your client base'
              : 'Find and book amazing talent for your next project'}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="stat-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-primary/20">
                  <Calendar className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.total_bookings || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Bookings</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-yellow-500/20">
                  <Clock className="h-6 w-6 text-yellow-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.pending_bookings || 0}</p>
                  <p className="text-sm text-muted-foreground">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-green-500/20">
                  <TrendingUp className="h-6 w-6 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.completed_bookings || 0}</p>
                  <p className="text-sm text-muted-foreground">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {user?.user_type === 'talent' && (
            <Card className="stat-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-secondary/20">
                    <DollarSign className="h-6 w-6 text-secondary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">${stats?.total_earnings?.toFixed(0) || 0}</p>
                    <p className="text-sm text-muted-foreground">Earnings</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {user?.user_type === 'talent' && (
            <Card className="stat-card col-span-2 md:col-span-1">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-accent/20">
                    <Star className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats?.rating?.toFixed(1) || '0.0'}</p>
                    <p className="text-sm text-muted-foreground">{stats?.review_count || 0} Reviews</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {user?.user_type === 'talent' ? (
            <>
              <Button
                variant="outline"
                className="h-auto p-6 flex flex-col items-start gap-2 rounded-2xl"
                onClick={() => navigate('/my-services')}
                data-testid="manage-services-btn"
              >
                <Briefcase className="h-6 w-6 text-primary" />
                <span className="font-bold">Manage Services</span>
                <span className="text-xs text-muted-foreground">Add or edit your offerings</span>
              </Button>

              <Button
                variant="outline"
                className="h-auto p-6 flex flex-col items-start gap-2 rounded-2xl"
                onClick={() => navigate('/settings')}
                data-testid="edit-profile-btn"
              >
                <Users className="h-6 w-6 text-primary" />
                <span className="font-bold">Edit Profile</span>
                <span className="text-xs text-muted-foreground">Update your portfolio</span>
              </Button>

              <Button
                variant="outline"
                className="h-auto p-6 flex flex-col items-start gap-2 rounded-2xl"
                onClick={() => navigate('/opportunities')}
                data-testid="find-gigs-btn"
              >
                <TrendingUp className="h-6 w-6 text-primary" />
                <span className="font-bold">Find Gigs</span>
                <span className="text-xs text-muted-foreground">Browse opportunities</span>
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                className="h-auto p-6 flex flex-col items-start gap-2 rounded-2xl"
                onClick={() => navigate('/browse')}
                data-testid="browse-talents-btn"
              >
                <Users className="h-6 w-6 text-primary" />
                <span className="font-bold">Browse Talents</span>
                <span className="text-xs text-muted-foreground">Find the perfect match</span>
              </Button>

              <Button
                variant="outline"
                className="h-auto p-6 flex flex-col items-start gap-2 rounded-2xl"
                onClick={() => navigate('/opportunities')}
                data-testid="post-opportunity-btn"
              >
                <Plus className="h-6 w-6 text-primary" />
                <span className="font-bold">Post Opportunity</span>
                <span className="text-xs text-muted-foreground">Create a gig or audition</span>
              </Button>

              <Button
                className="h-auto p-6 flex flex-col items-start gap-2 rounded-2xl btn-glow"
                onClick={() => navigate('/settings')}
                data-testid="become-talent-btn"
              >
                <Star className="h-6 w-6" />
                <span className="font-bold">Become a Talent</span>
                <span className="text-xs opacity-80">Start offering services</span>
              </Button>
            </>
          )}
        </div>

        {/* Recent Bookings */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle style={{ fontFamily: 'Syne, sans-serif' }}>Recent Bookings</CardTitle>
            <Button variant="ghost" onClick={() => navigate('/bookings')} data-testid="view-all-bookings-btn">
              View All
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentBookings.length > 0 ? (
              <div className="space-y-4">
                {recentBookings.map(booking => (
                  <div
                    key={booking.booking_id}
                    className="flex items-center justify-between p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => navigate('/bookings')}
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-xl bg-primary/10">
                        <Calendar className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {format(new Date(booking.start_time), 'MMM d, yyyy')} at {format(new Date(booking.start_time), 'h:mm a')}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          ${booking.total_price}
                        </p>
                      </div>
                    </div>
                    <Badge className={getStatusColor(booking.status)}>
                      {booking.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">No bookings yet</p>
                <Button onClick={() => navigate('/browse')} className="rounded-full">
                  Browse Talents
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
